import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '@/hooks/use-mobile';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import ErrorBoundary from '@/components/layout/ErrorBoundary';
import logoUrl from '@/assets/flathero-logo.svg';

/**
 * Decides whether to wrap routes in the consumer chrome (Header + Footer). The
 * /admin/* backoffice (and the legacy /analytics redirect target) render their
 * own full-screen shell via AdminLayout, so the consumer Header/Footer are
 * suppressed there. Everything else keeps the normal app chrome.
 */
export function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/') || pathname === '/analytics';
}

/**
 * SKUPIONY PRZEPŁYW — bez pełnego nagłówka zalogowanego i bez stopki
 * (CLAIM-02 + CLAIM-14, decyzja Piotra 29.09). Jedna definicja dla obu: nagłówek
 * pyta o to samo, co ta ramka, więc „co jest skupionym przepływem" ma jedno
 * miejsce zamiast dwóch predykatów, które rozjadą się przy następnej trasie.
 *
 * Szerszy niż `isFunnelPath` i to jest celowe. Kreator właściciela dołącza do
 * niego TYLKO w sprawie nagłówka i stopki; powłoki widoku (obsługa klawiatury
 * mobilnej) ani bramki czatu wsparcia NIE dotyka, bo:
 *   - powłoka blokuje przewijanie dokumentu i ma własne, długie uzasadnienie —
 *     wciąganie w nią kreatora to osobna zmiana, nie skutek uboczny tej,
 *   - `src/lib/support/gate.ts` wprost zostawia czat na `/owner/claim/review`
 *     (komentarz: „does NOT match /owner/claim/review"), więc rozszerzenie
 *     `isFunnelPath` cicho cofnęłoby tamtą decyzję.
 */
export function isFocusedFlowPath(pathname: string): boolean {
  return (
    isFunnelPath(pathname) ||
    pathname.startsWith('/owner/wizard') ||
    pathname.startsWith('/owner/claim')
  );
}

/**
 * The signup/onboarding funnel renders in a VIEWPORT SHELL (see below) and
 * without the marketing footer.
 */
export function isFunnelPath(pathname: string): boolean {
  return (
    pathname.startsWith('/onboarding') ||
    pathname.startsWith('/signup') ||
    pathname.startsWith('/login') || // includes /login-success
    pathname === '/hubabuba'
  );
}

/**
 * Wyszukiwarka na telefonie w widoku mapy: mapa zajmuje cały ekran, dokument się nie przewija
 * (SearchPage, `html.mapa-pelna`), więc stopka nie ma gdzie stać — renderowana wisiała pod mapą
 * i wychodziła przy każdym przeciągnięciu poza mapą (przegląd 375×740, 29.09).
 */
export function isFullScreenMapView(pathname: string, search: string, desktop: boolean): boolean {
  return !desktop && pathname === '/search' && new URLSearchParams(search).get('view') === 'map';
}

/**
 * Minimal stand-in if the Header itself crashes — keeps a branded, clickable bar
 * (and the page below) instead of blanking the whole app.
 */
const HeaderFallback = () => {
  const { t } = useTranslation();
  return (
    <header className="flex h-14 shrink-0 items-center border-b border-border bg-white px-4">
      <a href="/" aria-label={t('header.home_aria')} className="flex items-center">
        <img src={logoUrl} alt="" className="h-[1.575rem] w-auto" />
      </a>
    </header>
  );
};

export default function ChromeFrame({ children }: { children: React.ReactNode }) {
  const { pathname, search } = useLocation();
  const funnel = isFunnelPath(pathname);
  const desktop = useIsDesktop();

  /**
   * THE MOBILE KEYBOARD PROBLEM, and why this shell exists.
   *
   * Measured on Maciek's device (badge readouts, both iOS browsers). Opening
   * the keyboard does THREE things at once:
   *   1. the layout viewport stays its full height (clientHeight 684 / 714),
   *   2. the visual viewport shrinks (383 / 377) and PANS DOWN by offsetTop
   *      (67 / 148),
   *   3. the browser scrolls the document by roughly that same amount
   *      (scrollY 67 / 70).
   *
   * Sticky chrome pins to the LAYOUT viewport, so (2) pushed the header and
   * progress bar off-screen and we translated them back down by offsetTop.
   * But (3) moved the CONTENT up by the same distance — so the title, which
   * sits right below the bar, slid underneath it. Two compensations moving in
   * opposite directions: every previous attempt fixed one and broke the other,
   * which is exactly why this took so many rounds.
   *
   * The fix stops compensating and removes the cause. On funnel routes the app
   * becomes a shell that is POSITIONED ON THE VISIBLE AREA ITSELF: fixed, as
   * tall as the visual viewport, translated by the pan. Everything inside —
   * header, progress bar, form — moves as ONE unit, so nothing can drift
   * relative to anything else. The document is locked (no scrolling), so the
   * browser-imposed scroll of (3) has nowhere to happen; scrolling that IS
   * needed (a long form under a keyboard) happens inside the shell, where the
   * chrome sits outside the scrolling area and cannot be scrolled away.
   *
   * This is the standard mobile app-shell layout, and it makes the browser's
   * keyboard behaviour irrelevant rather than something we chase per browser.
   */
  useEffect(() => {
    const root = document.documentElement;
    const setVar = (name: string, value: string) => {
      if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
    };
    const publish = () => {
      const vv = window.visualViewport;
      setVar('--app-vvh', `${Math.round(vv?.height ?? window.innerHeight)}px`);
      setVar('--app-vv-top', `${Math.round(vv?.offsetTop ?? 0)}px`);
      // Retained for non-funnel pages that floor themselves to the viewport.
      setVar('--app-ih', `${window.innerHeight}px`);
    };
    publish();
    // iOS does not reliably fire resize/scroll at the END of a keyboard
    // transition (measured: a stale translate persisted with offsetTop already
    // back to 0). Polling is the only dependable source; the writes above are
    // no-ops when nothing changed, so this is cheap.
    const id = window.setInterval(publish, 100);
    const vv = window.visualViewport;
    window.addEventListener('resize', publish);
    vv?.addEventListener('resize', publish);
    vv?.addEventListener('scroll', publish);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('resize', publish);
      vv?.removeEventListener('resize', publish);
      vv?.removeEventListener('scroll', publish);
    };
  }, []);

  // Lock the document while the shell owns the screen — this is what denies the
  // browser the scroll it would otherwise impose when the keyboard opens.
  useEffect(() => {
    if (!funnel) return;
    document.documentElement.classList.add('funnel-locked');
    return () => document.documentElement.classList.remove('funnel-locked');
  }, [funnel]);

  if (isAdminPath(pathname)) {
    return <>{children}</>;
  }

  if (funnel) {
    return (
      <div
        data-viewport-shell
        className="fixed inset-x-0 top-0 z-0 flex flex-col overflow-hidden bg-background"
        style={{
          // Fallback is 100% — for a fixed element that resolves against the
          // viewport, so the shell is correct even on the very first paint,
          // before the publisher has written --app-vvh.
          height: 'var(--app-vvh, 100%)',
          // The pan (2). Applied to the WHOLE shell, so chrome and content can
          // never drift apart — the bug this replaces.
          transform: 'translateY(var(--app-vv-top, 0px))',
        }}
      >
        <ErrorBoundary label="header" resetKey={pathname} fallback={<HeaderFallback />}>
          <Header inViewportShell />
        </ErrorBoundary>
        {/* The only scrollable region. Chrome above it is untouchable. */}
        <main data-funnel-scroll className="flex-1 overflow-y-auto overscroll-contain">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-[min(100svh,var(--app-ih,9999px))] flex-col bg-background">
      <ErrorBoundary label="header" resetKey={pathname} fallback={<HeaderFallback />}>
        <Header />
      </ErrorBoundary>
      {/* Co najmniej wysokość okna pod nagłówkiem: dopóki leniwa trasa się nie wczyta, `main` jest
          pusty i stopka stała NA PIERWSZYM EKRANIE, a potem uciekała w dół — jedno przesunięcie
          CLS 0,56 na telefonie i 0,11 na pulpicie (pomiar 30.09, build produkcyjny, Slow 4G). */}
      <main className="min-h-[calc(min(100svh,var(--app-ih,9999px))-var(--app-header-h,57px))] flex-1">{children}</main>
      {/* Stopka konkuruje z przyklejonym działaniem kroku i odciąga z przepływu,
          który prosimy właściciela dokończyć (CLAIM-14). */}
      {!isFocusedFlowPath(pathname) && !isFullScreenMapView(pathname, search, desktop) && <Footer />}
    </div>
  );
}
